import React, { useState, useRef } from 'react'
import ConfirmationModal from '~/components/shared/ConfirmationModal'
import { useMessaging } from './MessagingProvider'
import { useMutation } from '@tanstack/react-query'
import { apiPost } from '~/util/api'
import { Button } from '~/lib/ui/button'
import { Input } from '~/lib/ui/input'
import { PaperClipIcon } from '@heroicons/react/24/outline'
import RichTextEditor from '~/lib/ui/rich-text-editor'
import useTranslation from '../shared/TranslationProvider'
import { useToast } from '~/lib/ui/toast'

const NewMessage = () => {
  const { t } = useTranslation('CoachMessageThread')
  const attachmentRef = useRef(null)
  const {
    queryClient,
    location,
    recipientCandidate,
    setRecipientCandidate,
    clearNewMessageMode,
    userIsCoach,
    setNewMessageType,
    setNewMessageMode,
    thread_params_key,
  } = useMessaging()
  const [newMessageSubject, setNewMessageSubject] = useState('')
  const editorRef = useRef(null)
  const [fileName, setFileName] = useState(null)
  const { toast } = useToast()

  const postNewThread = async (request) => {
    const response = await apiPost(`${location.pathname}.json`, request, true)
    return response
  }

  const createThreadMutation = useMutation({
    mutationFn: postNewThread,
    onSuccess: () => {
      window.location.query = ''
      setRecipientCandidate(null)
      queryClient.invalidateQueries(['messageThreads'])
      setNewMessageSubject('')
      clearNewMessageMode()
      editorRef.current.setContents([])
      toast({ description: t('message_sent') })
      return true
    },
    onError: (error) => {
      const description =
        error?.response?.status === 422
          ? t('file_unsafe')
          : t('an_error_occurred')

      toast({
        description,
        variant: 'destructive',
      })
    },
  })

  const { isPending } = createThreadMutation

  const cancel = () => {
    clearNewMessageMode()
    setRecipientCandidate(null)
    setNewMessageSubject('')
    editorRef.current.setContents([])
    if (userIsCoach) {
      setNewMessageType(null)
      setNewMessageMode(false)
    }
  }

  const [showConfirm, setShowConfirm] = useState(false)

  const actuallyCreateThread = () => {
    const attachment = attachmentRef.current?.files[0]
    const newMessageBody = editorRef.current.getSemanticHTML()
    const candidateAttributes =
      userIsCoach && recipientCandidate
        ? { candidate_id: recipientCandidate.id }
        : {}

    const form = new FormData()
    form.append(`${thread_params_key}[subject]`, newMessageSubject)
    form.append(`${thread_params_key}[body]`, newMessageBody)
    if (attachment) {
      form.append(`${thread_params_key}[attachment]`, attachment)
    }
    Object.entries(candidateAttributes).forEach(([key, value]) => {
      form.append(`${thread_params_key}[${key}]`, value)
    })

    createThreadMutation.mutate(form)
  }

  const createThread = () => {
    if (userIsCoach) {
      setShowConfirm(true)
    } else {
      actuallyCreateThread()
    }
  }

  const recipient = userIsCoach
    ? recipientCandidate?.full_name ||
      `${recipientCandidate?.first_name} ${recipientCandidate?.last_name}`
    : t('your_coach')

  if (userIsCoach && !recipientCandidate) return null

  const acceptFileTypes = ['.jpg', '.jpeg', '.png', '.pdf', '.docx', '.doc']

  const onFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
      setFileName(file.name)
    }
  }

  return (
    <div className="flex shrink-0 flex-col gap-y-4">
      <h2>
        {t('message')} {recipient}
      </h2>
      {userIsCoach && (
        <div className="-mt-2 text-sm text-mine-shaft-500">
          {[recipientCandidate?.role_name, recipientCandidate?.company_name]
            .filter(Boolean)
            .join(' • ')}
        </div>
      )}
      <div>
        <label>
          {t('subject')} <em>({t('required')})</em>
        </label>
        <Input
          value={newMessageSubject}
          onChange={(e) => setNewMessageSubject(e.target.value)}
          placeholder={t('enter_subject')}
        />
      </div>
      <div className="flex min-h-0 flex-col">
        <label>
          {t('message')} <em>({t('required')})</em>
        </label>
        <RichTextEditor ref={editorRef} className="h-60 w-full" />
      </div>
      <div className="flex flex-row justify-start gap-x-2">
        <Button onClick={createThread} variant="default">
          {t('send')}
        </Button>
        <div>
          <Button
            variant="outline"
            onClick={() => {
              attachmentRef.current.click()
            }}
            disabled={isPending}
          >
            <PaperClipIcon className="mr-1 h-5" />
            {fileName ? fileName : t('attach_file')}
          </Button>
          <input
            ref={attachmentRef}
            type="file"
            id="attachment-uploader"
            accept={acceptFileTypes}
            style={{ display: 'none' }}
            onChange={onFileSelect}
            disabled={isPending}
          />
        </div>
        <Button onClick={cancel} variant="outline">
          {t('cancel')}
        </Button>
      </div>
      {userIsCoach && (
        <ConfirmationModal
          open={showConfirm}
          onClose={() => setShowConfirm(false)}
          onConfirm={() => {
            setShowConfirm(false)
            actuallyCreateThread()
          }}
          title={t('confirm_send_title')}
          message={t('confirm_send_message', { name: recipient })}
        />
      )}
    </div>
  )
}
export default NewMessage
